import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
  IconChecks,
  IconCircleMinus,
  IconCopy,
  IconEdit,
  IconHome,
  IconTrash,
} from "@tabler/icons-react";
import dayjs from "dayjs";

import {
  Title,
  Text,
  Grid,
  Paper,
  Tooltip,
  Modal,
  ActionIcon,
  Divider,
  useMantineTheme,
  Flex,
  Stack,
  Badge,
} from "@mantine/core";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { useUseCaseBasic, useUseCaseMatchDetails } from "../../../hooks";
import Loading from "../../../ui/Loading";
import DatasetData from "../Data/DatasetData";
import { getUseCaseEventDetails } from "../../../api";
import DataDisplay from "../DataDisplay";
import Markdown from "../../Markdown/Markdown";

function Details({
  details,
}: {
  details: {
    __runid: string;
    __runseq: string;
    type: string;
    code: string;
    description: string;
    status: string;
    severity: string;
    details: any;
    eventdate: string;
    __recon: string;
    __access: string;
    __action: string;
    __externalid: string;
    __dataloadid: string;
    __text_col_1: string;
    __text_col_2: string;
    __text_col_3: string;
    __text_col_4: string;
    __text_col_5: string;
    __id: string;
    __matchids: string;
  };
}) {
  const theme = useMantineTheme();

  return (
    <Grid gutter={"xs"} columns={12}>
      <Grid.Col span={12}>
        <Flex gap="xs" align={"center"}>
          <Stack gap="0">
            <Text size="xs" fw={500} mb="xxs" inline>
              Type
            </Text>
            <Badge
              radius="xs"
              color={"gray"}
              // variant="light"
            >
              Event
            </Badge>
          </Stack>
          <Divider orientation="vertical" />
          <Stack gap="0">
            <Text size="xs" fw={500} mb="xxs" inline>
              Run ID
            </Text>
            <Text size="xs" inline c="dimmed">
              {details.__runid}
            </Text>
          </Stack>
          <Divider orientation="vertical" />
          <Stack gap="0">
            <Text size="xs" fw={500} mb="xxs" inline>
              Run Seq
            </Text>
            <Text size="xs" inline c="dimmed">
              {details.__runseq}
            </Text>
          </Stack>
          <Divider orientation="vertical" />
          <Stack gap="0">
            <Text size="xs" fw={500} mb="xxs" inline>
              ID
            </Text>
            <Text size="xs" inline c="dimmed">
              {details.__id}
            </Text>
          </Stack>

          <Divider orientation="vertical" />
          <Stack gap="0">
            <Text size="xs" fw={500} mb="xxs" inline>
              External ID
            </Text>
            <Text size="xs" inline c="dimmed">
              {details.__externalid}
            </Text>
          </Stack>

          {details.eventdate && (
            <>
              <Divider orientation="vertical" />
              <Stack gap="0">
                <Text size="xs" fw={500} mb="xxs" inline>
                  Event Date
                </Text>
                <Text size="xs" inline c="dimmed">
                  {dayjs(details.eventdate).format("YYYY-MM-DD HH:mm:ss")}
                </Text>
              </Stack>
            </>
          )}
        </Flex>
      </Grid.Col>
    </Grid>
  );
}

function EventDetail({ detail }: { detail: any }) {
  const theme = useMantineTheme();

  return (
    <Stack mt="xs" gap="0" p="xs">
      <Flex>
        <Title
          mt="xs"
          order={5}
          py={0}
          px={theme.spacing.sm}
          style={{
            borderLeft: `4px solid ${theme.colors.emerald[0]}`,
          }}
        >
          {detail.code}
        </Title>
      </Flex>
      <Divider mt="xs" />

      <Grid align="top" gutter={"xs"} mb="xs">
        <Grid.Col span={6}>
          <Stack mt="xs" h="100%" gap="0" p="xs" bg="#FAF7F3">
            {detail.description && (
              <Markdown description={detail.description} />
            )}
          </Stack>
        </Grid.Col>

        <Grid.Col span={6}>
          <Stack mt="xs" h="100%" gap="0" p="xs" bg="#FFF2E0">
            <DataDisplay
              data={
                detail.data && Array.isArray(detail.data) ? detail.data : []
              } // Pass as array
              table="details"
              definition={[]}
              links={[]}
              enableSelection={false}
              // ignoreColumns={dataset.id === "EVENT" ? ["details"] : []}
            />
          </Stack>
        </Grid.Col>
      </Grid>
    </Stack>
  );
}

export default function Event() {
  // Search params
  let [searchParams] = useSearchParams();
  let id = searchParams.get("id"),
    entityId = searchParams.get("entityId"),
    eventId = searchParams.get("eventId"),
    runId = searchParams.get("runId"),
    runSeq = searchParams.get("runSeq"),
    recon = searchParams.get("recon"),
    access = searchParams.get("access");

  // Theme, query client and navigation
  const theme = useMantineTheme();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: useCase } = useUseCaseBasic(entityId, id);

  const [eventDetails, setEventDetails] = React.useState<{
    __runid: string;
    __runseq: string;
    type: string;
    code: string;
    description: string;
    status: string;
    severity: string;
    details: any;
    eventdate: string;
    __recon: string;
    __access: string;
    __action: string;
    __externalid: string;
    __dataloadid: string;
    __text_col_1: string;
    __text_col_2: string;
    __text_col_3: string;
    __text_col_4: string;
    __text_col_5: string;
    __id: string;
    __matchids: string;
  }>();

  React.useEffect(() => {
    // console.log("fetching on mount", fetchOnMount);
    const getData = async () => {
      let values = await getUseCaseEventDetails(
        entityId,
        id,
        runId,
        runSeq,
        access,
        recon,
        eventId
      );
      setEventDetails(values);
    };
    getData();
  }, [entityId, id, recon, runId, runSeq, access, eventId]);

  if (useCase) {
    return (
      <>
        <Stack gap="0">
          <div id="header">
            <Grid
              align="flex-end"
              gutter={"xs"}
              mt="xs"
              mb="xs"
              // mb="xs"
              style={{
                display: "flex",
                // borderLeft: `${theme.spacing.xxs} solid ${theme.colors.emerald[7]}`,
              }}
              columns={12}
            >
              <Grid.Col
                span={2}
                mr="xs"
                style={{ borderRight: `2px solid ${theme.colors.dark[3]}` }}
              >
                <Flex align={"end"} gap={theme.spacing.xs}>
                  <Stack gap={0}>
                    <Title order={3} c="dark">
                      {useCase ? useCase.name : "Use Case"}
                    </Title>
                    {useCase && (
                      <Text size="sm" c="dark">
                        {useCase.displayName}
                      </Text>
                    )}
                  </Stack>
                </Flex>
              </Grid.Col>
              <Grid.Col span={9}>
                <Flex align={"flex-end"} gap="xs">
                  {eventDetails && <Details details={eventDetails} />}
                </Flex>
              </Grid.Col>
            </Grid>
          </div>
        </Stack>
        <Divider mt="xs" />

        {eventDetails?.details ? (
          Array.isArray(eventDetails.details) ? (
            <>
              <Stack gap="0" mt="xs" mb="lg">
                {eventDetails.details.map((d, index) => {
                  return <EventDetail key={index} detail={d} />;
                })}
              </Stack>
            </>
          ) : (
            <></>
          )
        ) : (
          <></>
        )}
      </>
    );
  } else {
    return (
      <Grid align="center" gutter={"xs"} mb="xs" mt="lg">
        <Grid.Col span={4}>
          <Loading />
        </Grid.Col>
      </Grid>
    );
  }
}
